import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppBar from "@mui/material/AppBar";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import PrimaryButton from "components/PrimaryButton";
import StyledTextField from "components/StyledTextField";
import Transition from "components/Transition";
import { MachineType, Maybe, UpdateMachineInputType } from "operations/schema/schema";
import { FC, useCallback, useState } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { setOpen } from "store/slices/dialog.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";

type EditMachineLocationDialogProps = {
  machine: Maybe<MachineType>;
  loading: boolean;
  externalId: string;
  updateMachine: (
    machine: UpdateMachineInputType
  ) => Promise<{ success: boolean; queued: boolean; abort: boolean }>;
};

export const EditMachineLocationDialog: FC<EditMachineLocationDialogProps> = (props) => {
  const { machine, loading, externalId, updateMachine } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.dialog.editMachineLocation);

  const [newLocation, setNewLocation] = useState("");
  const machineInstallDate = machine?.installDate;

  const isValidW3W = useCallback(() => {
    if (newLocation.startsWith("///")) {
      const tempNewLocation = newLocation.replace("///", "");
      const tempNewLocationArray = tempNewLocation.split(".");
      const words = tempNewLocationArray[0] && tempNewLocationArray[1] && tempNewLocationArray[2];

      if (!/\s/.test(tempNewLocation) && tempNewLocationArray.length === 3 && words) {
        return true;
      }

      return false;
    }

    return true;
  }, [newLocation]);

  const handleClose = useCallback(() => {
    dispatch(setOpen({ dialogName: "editMachineLocation", open: false }));
  }, [dispatch]);

  const updateMachineCb = useCallback(
    async (machine: UpdateMachineInputType) => {
      const ret = await updateMachine(machine);
      if (ret.success) {
        handleClose();
        if (ret.queued) {
          dispatch(addSnackbarMessage({ key: "UpdateMachine-stored" }));
        } else {
          dispatch(addSnackbarMessage({ key: "UpdateMachine-success" }));
        }
      } else if (!ret.success && !ret.abort) {
        dispatch(addSnackbarMessage({ key: "UpdateMachine-fail" }));
      }
    },
    [dispatch, handleClose, updateMachine]
  );

  if (!machine) {
    handleClose();
    return null;
  }
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      data-testid="EditMachineLocationDialog"
    >
      <BackdropPrimaryMain open={loading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="EditMachineLocationDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="AppBar-label" paddingRight="36px">
            {`${externalId} `} / <FormattedMessage id="visit.editMachineLocation" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <Card>
        <CardHeader
          sx={{
            fontSize: "1.2rem",
          }}
          subheader={
            <>
              <Typography fontWeight={700}>{machine.name}</Typography>
              <Typography>{machine.machineNo}</Typography>
              {machine.idNo && <Typography>{machine.idNo}</Typography>}
              {machine.location && <Typography>{machine.location}</Typography>}
              {machineInstallDate && (
                <Typography>
                  {`${intl.formatMessage({ id: "general.installed" })} `}
                  <FormattedDate
                    value={machineInstallDate}
                    day="numeric"
                    month="long"
                    year="numeric"
                  />
                </Typography>
              )}
            </>
          }
        />
        <CardContent sx={{ paddingTop: 0 }}>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              <StyledTextField
                value={newLocation}
                label={intl.formatMessage({ id: "equipmentEdit.newLocation" })}
                onChange={(e) => {
                  setNewLocation(e.target.value);
                }}
                error={!isValidW3W()}
                helperText={
                  isValidW3W()
                    ? ""
                    : intl.formatMessage({
                        id: "equipmentEdit.invalidWhat3Words",
                      })
                }
                data-testid="EditMachineLocationDialog-TextField"
              />
            </Grid>
            <Grid item>
              <PrimaryButton
                key={`${!newLocation || loading}`}
                disabled={!newLocation || loading}
                fullWidth
                data-testid="EditMachineLocationDialog-SaveButton"
                onClick={() => {
                  updateMachineCb({
                    id: machine.id || "",
                    idNo: machine.idNo!,
                    machineNo: machine.machineNo!,
                    modelGuid: machine.modelGuid!,
                    addressRowNo: machine.addressRowNo,
                    installDate: machine.installDate,
                    information: machine.details?.information ?? "",
                    location: newLocation,
                  });
                }}
              >
                <FormattedMessage id="general.save" />
              </PrimaryButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Dialog>
  );
};
